export type LogLevel = "debug" | "info" | "warn" | "error";

interface LogInput {
  message: string;
  requestId?: string;
  httpMethod?: string;
  path?: string;
  handler?: string;
  statusCode?: number | string;
  durationMs?: number;
}

function environment(): string {
  const env =
    process.env["REACT_APP_ENV"] ?? process.env["NODE_ENV"] ?? "local";
  const normalized = env.toLowerCase();

  if (normalized === "production") return "prod";
  if (normalized === "development" || normalized === "test") return "local";
  if (normalized === "intp" || normalized === "integration") return "int";
  if (["local", "dev", "int", "prod"].includes(normalized)) return normalized;

  return normalized || "local";
}

function quoteMessage(message: string): string {
  return `"${message
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/[\r\n]+/g, " ")}"`;
}

function addOptionalField(
  fields: string[],
  key: string,
  value: string | number | undefined,
): void {
  if (value === undefined || value === null || value === "") return;
  fields.push(`${key}=${String(value)}`);
}

export function formatLogLine(level: LogLevel, input: LogInput): string {
  const fields = [
    `Timestamp=${new Date().toISOString()}`,
    `LogLevel=${level}`,
    `Environment=${environment()}`,
  ];

  addOptionalField(fields, "RequestId", input.requestId);
  addOptionalField(fields, "HttpMethod", input.httpMethod);
  addOptionalField(fields, "Path", input.path);
  addOptionalField(fields, "Handler", input.handler);
  addOptionalField(fields, "StatusCode", input.statusCode);
  addOptionalField(fields, "DurationMs", input.durationMs);
  fields.push(`Message=${quoteMessage(input.message)}`);

  return fields.join(" || ");
}

function emit(level: LogLevel, input: LogInput): void {
  const line = formatLogLine(level, input);

  if (level === "error") {
    console.error(line);
    return;
  }
  if (level === "warn") {
    console.warn(line);
    return;
  }
  if (level === "debug") {
    console.debug(line);
    return;
  }
  console.log(line);
}

export const webLogger = {
  debug(input: LogInput): void {
    emit("debug", input);
  },
  info(input: LogInput): void {
    emit("info", input);
  },
  warn(input: LogInput): void {
    emit("warn", input);
  },
  error(input: LogInput): void {
    emit("error", input);
  },
};
